import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { X } from 'lucide-react';

// Modules
import { animateNamedTransition, fadeIn, fadeOut } from '@/modules/animation';

// Related Components
import ModalContext from './modal/context';
import ModalOpenButton from './modal/open_button';

function getScrollbarWidth() {
  if (typeof window === 'undefined') return 0;

  return Math.max(
    0,
    window.innerWidth - document.documentElement.clientWidth
  );
}

export default class Modal extends React.Component {
  static _lockCount = 0;

  static _saved = {
    bodyOverflow: null,
    bodyPaddingRight: null,
    htmlOverflow: null,
  };

  static propTypes = {
    id: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.oneOf(['sm', 'lg', 'xlg', 'custom']),
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onCancel: PropTypes.func,
    isOpen: PropTypes.bool,
    showX: PropTypes.bool,
    closeOnEscape: PropTypes.bool,
    closeOnOverlayClick: PropTypes.bool,
    showOverlay: PropTypes.bool,
    modalTransitionIn: PropTypes.string,
    modalTransitionOut: PropTypes.string,
  };

  static defaultProps = {
    id: '',
    className: '',
    size: undefined,
    onOpen: () => {},
    onClose: () => {},
    onCancel: () => {},
    isOpen: false,
    showX: true,
    closeOnEscape: true,
    closeOnOverlayClick: true,
    showOverlay: true,
    modalTransitionIn: 'transition.slideUpBigIn',
    modalTransitionOut: 'transition.slideDownBigOut',
  };

  constructor(props) {
    super(props);

    this.overlay = React.createRef();
    this.modal = React.createRef();

    this.state = {
      isOpen: props.isOpen || false,
    };

    this.node = document.createElement('section');
    document.body.appendChild(this.node);
  }

  componentDidMount() {
    if (this.state.isOpen) {
      this.handleOpenModal();
    }
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props;

    if (isOpen !== undefined && prevProps.isOpen !== isOpen) {
      if (isOpen) {
        this.handleOpenModal();
      } else {
        this.handleCloseModal();
      }
    }
  }

  componentWillUnmount() {
    this.unlockScroll(true);
    document.removeEventListener('keydown', this.handleKeydown);

    if (this.node && document.body.contains(this.node)) {
      document.body.removeChild(this.node);
    }
  }

  lockScroll = () => {
    if (typeof document === 'undefined') return;

    if (Modal._lockCount === 0) {
      const body = document.body;
      const html = document.documentElement;

      Modal._saved.bodyOverflow = body.style.overflow || '';
      Modal._saved.bodyPaddingRight = body.style.paddingRight || '';
      Modal._saved.htmlOverflow = html.style.overflow || '';

      const scrollbarWidth = getScrollbarWidth();

      if (scrollbarWidth > 0) {
        const currentPaddingRight = (
          parseInt(window.getComputedStyle(body).paddingRight, 10) || 0
        );

        body.style.paddingRight = (
          `${currentPaddingRight + scrollbarWidth}px`
        );
      }

      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
    }

    Modal._lockCount += 1;
  };

  unlockScroll = (force = false) => {
    if (typeof document === 'undefined') return;

    if (force) {
      Modal._lockCount = 1;
    }

    if (Modal._lockCount > 0) {
      Modal._lockCount -= 1;

      if (Modal._lockCount === 0) {
        const body = document.body;
        const html = document.documentElement;

        body.style.overflow = Modal._saved.bodyOverflow || '';
        body.style.paddingRight = Modal._saved.bodyPaddingRight || '';
        html.style.overflow = Modal._saved.htmlOverflow || '';

        Modal._saved.bodyOverflow = null;
        Modal._saved.bodyPaddingRight = null;
        Modal._saved.htmlOverflow = null;
      }
    }
  };

  handleOpenModal = () => {
    const {
      showOverlay,
      modalTransitionIn,
      onOpen,
    } = this.props;

    this.lockScroll();

    this.setState({
      isOpen: true,
    }, () => {
      if (showOverlay && this.overlay.current) {
        fadeIn(this.overlay.current, { duration: 150 });
      }

      if (this.modal.current) {
        animateNamedTransition(
          this.modal.current,
          modalTransitionIn,
          {
            opening: true,
            delay: 150,
            duration: 250,
          }
        ).then(() => {
          onOpen();
          document.addEventListener('keydown', this.handleKeydown);
        });
      } else {
        onOpen();
        document.addEventListener('keydown', this.handleKeydown);
      }
    });
  };

  handleCloseModal = () => {
    const {
      onClose,
      modalTransitionOut,
    } = this.props;
    const { isOpen } = this.state;

    if (!isOpen) {
      return;
    }

    const modalAnimation = this.modal.current
      ? animateNamedTransition(
          this.modal.current,
          modalTransitionOut,
          {
            opening: false,
            duration: 250,
          }
        )
      : Promise.resolve();

    const finalizeClose = () => {
      this.unlockScroll();

      this.setState({
        isOpen: false,
      }, () => onClose());

      document.removeEventListener('keydown', this.handleKeydown);
    };

    if (this.overlay.current) {
      fadeOut(this.overlay.current, { duration: 250 }).then(finalizeClose);
      return;
    }

    modalAnimation.then(finalizeClose);
  };

  handleOverlayClick = (event) => {
    const {
      closeOnOverlayClick,
      onCancel,
    } = this.props;

    if (
      closeOnOverlayClick &&
      event.target === this.overlay.current
    ) {
      this.handleCloseModal();
      onCancel();
    }
  };

  handleKeydown = (event) => {
    const {
      closeOnEscape,
      onCancel,
    } = this.props;

    if (closeOnEscape && event.which === 27) {
      this.handleCloseModal();
      onCancel();
    }
  };

  get opener() {
    const { children } = this.props;

    return React.Children
      .toArray(children)
      .find(
        child => (
          React.isValidElement(child) &&
          child.type === ModalOpenButton
        )
      ) || null;
  }

  get modalChildren() {
    const { children } = this.props;

    return React.Children
      .toArray(children)
      .filter(
        child => (
          !React.isValidElement(child) ||
          child.type !== ModalOpenButton
        )
      );
  }

  get classNames() {
    const {
      className,
      size,
    } = this.props;

    return classNames('modal', {
      [className]: className,
      'is-small': size === 'sm',
      'is-large': size === 'lg',
      'is-xlarge': size === 'xlg',
      'is-custom': size === 'custom',
    });
  }

  get container() {
    const {
      id,
      size,
      showX,
      showOverlay,
    } = this.props;

    return (
      <React.Fragment>
        {
          showX &&
          <X
            className='dark absolute top-10 right-15 hover-primary cursor-pointer'
            size={48}
            onClick={this.handleCloseModal}
          />
        }

        <div
          id={id}
          className='modal-container'
        >
          {
            showOverlay
              ? (
                <div
                  className={classNames(
                    'overlay',
                    {
                      'is-small': size === 'sm',
                    }
                  )}
                  ref={this.overlay}
                  onMouseDown={this.handleOverlayClick}
                >
                  <div
                    className={this.classNames}
                    ref={this.modal}
                  >
                    {this.modalChildren}
                  </div>
                </div>
              )
              : (
                <div
                  className={this.classNames}
                  ref={this.modal}
                >
                  {this.modalChildren}
                </div>
              )
          }
        </div>
      </React.Fragment>
    );
  }

  render() {
    const { isOpen } = this.state;

    const contextValue = {
      openModal: this.handleOpenModal,
      closeModal: this.handleCloseModal,
    };

    return (
      <ModalContext.Provider value={contextValue}>
        {isOpen && createPortal(this.container, this.node)}
        {this.opener}
      </ModalContext.Provider>
    );
  }
}
